import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Inbox, Settings2, LogOut, Loader2, BellRing } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CalendarTab from "./CalendarTab";
import InquiriesTab from "./InquiriesTab";
import FollowUpsTab from "./FollowUpsTab";
import SettingsTab from "./SettingsTab";

const TABS = [
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "inquiries", label: "Inquiries", icon: Inbox },
  { id: "followups", label: "Follow-ups", icon: BellRing },
  { id: "settings", label: "Settings & Content", icon: Settings2 },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("calendar");

  const remindersQ = useQuery({
    queryKey: ["admin-reminders"],
    queryFn: () => api.get("/admin/reminders").then((r) => r.data),
    enabled: !!user,
    retry: false,
  });
  const pendingReminders = remindersQ.data?.reminders?.length || 0;

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-bone2" data-testid="admin-dashboard">
      <Seo title="Admin — The Owl's Nest" noindex />
      <header className="sticky top-0 z-40 border-b border-black/5 bg-bone/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3.5 sm:px-8">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-stone-500 sm:block">{user.email}</span>
            <button onClick={async () => { await logout(); navigate("/admin/login"); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-black/30">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 sm:px-8" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`relative flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors
                ${tab === t.id ? "border-ink text-ink" : "border-transparent text-stone-400 hover:text-stone-600"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
              {t.id === "followups" && pendingReminders > 0 && (
                <span className="rounded-full bg-gold px-1.5 py-0.5 text-[10px] font-bold text-ink" data-testid="admin-followups-badge">
                  {pendingReminders}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10">
        {pendingReminders > 0 && tab !== "followups" && (
          <button onClick={() => setTab("followups")} data-testid="admin-reminder-alert"
            className="mb-6 flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl bg-ink p-5 text-left transition-transform hover:-translate-y-0.5 sm:px-6">
            <div className="flex items-start gap-4">
              <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <div>
                <p className="text-sm font-semibold text-bone">
                  {pendingReminders === 1 ? "Time to reach out again" : `${pendingReminders} follow-ups waiting`}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-stone-400">
                  {remindersQ.data.reminders[0].name} held their {String(remindersQ.data.reminders[0].event_type || "event").toLowerCase()} here on{" "}
                  {fmtDate(remindersQ.data.reminders[0].original_date)} — reach out about this year's date.
                  {pendingReminders > 1 ? ` Plus ${pendingReminders - 1} more.` : ""}
                </p>
              </div>
            </div>
            <span className="rounded-full bg-gold px-4 py-2 text-xs font-bold text-ink">View follow-ups</span>
          </button>
        )}
        {tab === "calendar" && <CalendarTab />}
        {tab === "inquiries" && <InquiriesTab />}
        {tab === "followups" && <FollowUpsTab />}
        {tab === "settings" && <SettingsTab user={user} />}
      </main>
    </div>
  );
}
